import { memo, useEffect } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  EmptyIndicator,
  LoadingIndicator,
  MoreOptions,
  TimeIndicator
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { DropdownButton } from 'components/common/dropdown';

import type { GetChapterPreviousVersionsRes } from 'shared-types/responses';

interface Props {
  chapterId: number;
  chapterSlug: string;
  onPagesChange: (pages: number) => void;
  onPreview: (url: string) => void;
  queryParams: string;
}

const EditorVersionHistoryTableChapter = ({
  chapterId,
  chapterSlug,
  onPagesChange,
  onPreview,
  queryParams
}: Props) => {
  const {
    language,
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const { data, loading } = useApiData<GetChapterPreviousVersionsRes>({
    url:
      `/admin/courses/${course.id}` +
      `/versions-chapter-versions/${chapterId}` +
      `?${queryParams}`
  });

  useEffect(() => {
    if (!loading && data?.pages) onPagesChange(data?.pages || 1);
  }, [data, loading]);

  return loading ? (
    <LoadingIndicator />
  ) : !data?.previousVersions.length ? (
    <EmptyIndicator icon="history" title={messages.coursesNoVersions} />
  ) : (
    <BoxOverflowWrapper>
      <BoxTable>
        <BoxTableHeader>
          <BoxTableCell>{messages.analyticsDateFrom}</BoxTableCell>
          <BoxTableCell>{messages.analyticsDateTo}</BoxTableCell>
          <BoxTableCell>{messages.commonBlocks}</BoxTableCell>
          <BoxTableCell shrink={true} />
        </BoxTableHeader>

        {data?.previousVersions.map((version) => {
          return (
            <BoxTableRow key={version.id}>
              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                <span style={{ marginRight: 10 }}>
                  {getFormattedDate({
                    date: version.versionNumber,
                    dateFormat: 'dd.MM.yyyy HH:mm:ss'
                  })}
                </span>

                <span
                  style={{
                    color: theme.textColorLighter,
                    fontWeight: 400,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {'('}
                  <TimeIndicator
                    language={language}
                    timeString={version.versionNumber}
                  />
                  {')'}
                </span>
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {version.createdAt ? (
                  <>
                    <span style={{ marginRight: 10 }}>
                      {getFormattedDate({
                        date: version.createdAt,
                        dateFormat: 'dd.MM.yyyy HH:mm:ss'
                      })}
                    </span>

                    <span
                      style={{
                        color: theme.textColorLighter,
                        fontWeight: 400,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {'('}
                      <TimeIndicator
                        language={language}
                        timeString={version.createdAt}
                      />
                      {')'}
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      style={{
                        color: theme.textColorLighter,
                        fontWeight: 400,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {`(${messages.coursesCurrentVersion})`}
                    </span>
                  </>
                )}
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {getMessageWithPlaceholders({
                  message: messages.coursesBlocksNumberOf,
                  placeholders: {
                    numberOfBlocks: version.blockPositions.length
                  }
                })}
              </BoxTableCell>

              <BoxTableCell>
                {version.createdAt ? (
                  <MoreOptions renderAsFixed={true} styleContentButtons={false}>
                    <DropdownButton
                      category="light"
                      icon="eye"
                      onClick={() =>
                        onPreview(
                          `/admin/courses/${course.slug}` +
                            `/edit/${chapterSlug}` +
                            `?chapterPreviewVersion=${version.versionNumber}`
                        )
                      }
                      text={messages.coursesPreviewInEditor}
                    />
                  </MoreOptions>
                ) : null}
              </BoxTableCell>
            </BoxTableRow>
          );
        })}
      </BoxTable>
    </BoxOverflowWrapper>
  );
};

export default memo(EditorVersionHistoryTableChapter);
